import { useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { FilesField } from '../../../../components/inputs/FilesField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { CASE_FILE_ACCEPT, REPORT_FILE_MAX_BYTES, type PostOperationReportListItem } from '../caseTypes';
import { postOperationReportsApi } from '../postOperationReportsApi';
import { fileAll } from '../utils/fileAll';
import { samplePdf, sampleReport } from '../utils/sampleCase';

const TITLE_MAX = 200;
const SUMMARY_MAX = 20000;

/**
 * Writes a Post Operation Report on a case, or corrects one.
 *
 * <b>The summary is PLAIN TEXT for now</b> — the rich-text editor was asked for and deferred the
 * same day (12 Oct 2026) while the shared editor was being reworked.
 *
 * <b>Files ride on the CREATE only</b>, and only for a reader holding `post-operation-reports.attach`:
 * the report is saved FIRST, then each file in its own request. A file that fails does not undo the
 * report — it is named, and attached again from the report's own page. An edit carries no files;
 * they are managed on the report's Attachments tab.
 */
export function PostOperationReportModal({
  caseId,
  editing,
  onClose,
  onSaved,
}: {
  caseId: string;
  editing: PostOperationReportListItem | null;
  onClose: () => void;
  /** `failed` names the files that did not file; the report itself was saved. */
  onSaved: (saved: PostOperationReportListItem, failed: { name: string; reason: string }[]) => void;
}) {
  const canAttach = usePermission('post-operation-reports.attach');
  const takesFiles = canAttach && editing === null;

  const [title, setTitle] = useState(editing?.title ?? '');
  const [summary, setSummary] = useState(editing?.summary ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const oversize = files.some((file) => file.size > REPORT_FILE_MAX_BYTES);
  const valid = title.trim().length > 0 && summary.trim().length > 0 && !oversize;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { title: title.trim(), summary: summary.trim() };

    try {
      const saved = editing
        ? await postOperationReportsApi.update(editing.id, request)
        : await postOperationReportsApi.create(caseId, request);

      let failed: { name: string; reason: string }[] = [];

      if (takesFiles && files.length > 0) {
        setProgress(0);
        failed = await fileAll(postOperationReportsApi.files(saved.id), files, setProgress);
      }

      onSaved(saved, failed);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the report.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'post-operation-report' });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.title}` : 'New Post Operation Report'}
      size="2xl"
      id="post-operation-report-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleReport();
          setTitle(sample.title);
          setSummary(sample.summary);
          if (takesFiles) setFiles([samplePdf('sample-report-document.pdf')]);
        },
        disabled: saving,
      }}
      confirm={{ id: 'post-operation-report-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="post-operation-report-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="post-operation-report-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="post-operation-report-title"
          label="Title"
          required
          description="What the report is about — the operation, as the team calls it."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          disabled={saving}
          error={error?.fieldError('title')}
        />

        <Textarea
          id="post-operation-report-summary"
          label="Summary"
          required
          description="What happened on the operation, and what came of it."
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          maxLength={SUMMARY_MAX}
          minRows={8}
          disabled={saving}
          error={error?.fieldError('summary')}
        />

        {takesFiles ? (
          <FilesField
            id="post-operation-report-files"
            label="Attachments"
            description={`PDF, PNG, JPEG or MP4, up to ${formatBytes(REPORT_FILE_MAX_BYTES)} each. Filed after the report is saved, one at a time.`}
            accept={CASE_FILE_ACCEPT}
            maxBytes={REPORT_FILE_MAX_BYTES}
            files={files}
            onChange={setFiles}
            disabled={saving}
          />
        ) : null}
      </div>
    </Modal>
  );
}
